import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { contentAPI, publicCallsAPI } from '../services/api'
import type { Content as ContentType, PublicCall } from '../types'
import { apiErrorMessage } from '../utils/apiError'
import { callShareText } from '../utils/callShareText'
import { RECORD_MISMATCH_WARNING } from '../utils/draftFlags'

const PLATFORMS = [
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'blog', label: 'Blog' },
  { id: 'instagram', label: 'Instagram' },
] as const

/**
 * A longer post from one of the user's own calls, opened from its receipt's
 * "Write a longer post". The call's facts are fixed: shown here as the share
 * text, and sent as the call's id, so the server reads them from the record.
 * The draft may add commentary, but quotes the result, R and close reason
 * exactly as recorded, before fees.
 */
const CallPostGenerator = ({ callId }: { callId: number }) => {
  const [call, setCall] = useState<PublicCall | null>(null)
  const [failed, setFailed] = useState(false)
  const [platform, setPlatform] = useState<string>('linkedin')
  const [generating, setGenerating] = useState(false)
  const [generated, setGenerated] = useState<ContentType | null>(null)

  useEffect(() => {
    let cancelled = false
    setCall(null)
    setFailed(false)
    publicCallsAPI
      .get(callId)
      .then((c) => !cancelled && setCall(c))
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [callId])

  const facts = call ? callShareText(call) : null

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      setGenerated(await contentAPI.generate({ content_type: platform, call_id: callId }))
      toast.success('Draft written')
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to generate content'))
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow px-4 py-3" data-testid="call-post">
      <h2 className="text-base font-semibold text-gray-900">A longer post from call #{callId}</h2>
      {failed && <p className="mt-2 text-sm text-red-700">This call could not be loaded.</p>}
      {!failed && !facts && <p className="mt-2 text-sm text-gray-500">Loading the call…</p>}
      {facts && (
        <>
          <p className="mt-2 text-sm text-gray-600">
            The call's facts are fixed. The draft may add commentary, but quotes the result, R and close reason exactly
            as recorded, before fees.
          </p>
          <pre
            className="mt-2 whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-gray-50 p-3 font-sans text-sm text-gray-800"
            data-testid="call-post-facts"
          >
            {facts}
          </pre>
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <div className="min-w-[10rem]">
              <label htmlFor="call-post-platform" className="mb-2 block text-sm font-medium text-gray-700">
                Platform
              </label>
              <select
                id="call-post-platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {PLATFORMS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="rounded-md bg-primary-600 px-6 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {generating ? 'Writing…' : 'Write the draft'}
            </button>
          </div>
        </>
      )}
      {generated && (generated.record_mismatch ?? []).length > 0 && (
        // The draft is saved all the same; this says it can't be posted as it is.
        <div
          role="alert"
          className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          data-testid="call-post-record-mismatch"
        >
          <p className="font-semibold">{RECORD_MISMATCH_WARNING}</p>
          <p className="mt-1 text-xs">As recorded on the receipt: {(generated.record_mismatch ?? []).join(', ')}.</p>
        </div>
      )}
      {generated && (
        <div className="mt-3 flex items-center gap-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <span>Saved as a draft.</span>
          <Link
            to="/content"
            state={{ openIds: [generated.id] }}
            className="ml-auto font-medium underline hover:text-green-900"
          >
            View in Library
          </Link>
        </div>
      )}
    </div>
  )
}

export default CallPostGenerator
